/**
 * Shared worker form status helpers — keep dashboard buttons aligned with truth.
 */

export const FORM_ADMIN_COMPLETE_STATUSES = ['verified', 'signed_off', 'reviewed', 'approved'];

// Any state where the worker has finished their part and an admin/manager decision
// is pending. These must be OWNED BY ADMIN, never surfaced as a worker "Start/Continue"
// action. Aligned with the canonical admin-review keys in utils/statusLabels.js so the
// worker dashboard and admin views agree on who must act next.
export const FORM_AWAITING_REVIEW_STATUSES = [
  'submitted',
  'awaiting_admin_review',
  'awaiting_review',
  'pending_review',
  'awaiting_company_countersignature',
  'awaiting_schema_upgrade',
];

export const FORM_CORRECTION_STATUSES = [
  'returned_for_correction',
  'reopened_for_worker_correction',
  'amendment_requested',
  'rejected',
];

export function isFormAdminComplete(status) {
  return FORM_ADMIN_COMPLETE_STATUSES.includes(status);
}

export function isFormAwaitingReview(status) {
  return FORM_AWAITING_REVIEW_STATUSES.includes(status);
}

/** Worker still needs to start, continue, or correct the form. */
export function isFormWorkerActionRequired(status) {
  return !isFormAdminComplete(status) && !isFormAwaitingReview(status);
}

export function getFormActionLabel(form) {
  const status = form?.status;
  if (isFormAwaitingReview(status)) return 'View submission';
  if (FORM_CORRECTION_STATUSES.includes(status)) return 'Correct';
  if (status === 'in_progress') return 'Continue';
  return 'Start';
}

export function getFormActionButtonClass(form) {
  const status = form?.status;
  if (isFormAwaitingReview(status)) {
    return 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200';
  }
  if (FORM_CORRECTION_STATUSES.includes(status)) return 'bg-red-600 hover:bg-red-700';
  if (status === 'in_progress') return 'bg-amber-600 hover:bg-amber-700';
  return 'bg-blue-600 hover:bg-blue-700';
}

export function getFormRowAccentClass(form) {
  const status = form?.status;
  if (isFormAwaitingReview(status)) {
    return 'border-purple-200/80 bg-gradient-to-br from-purple-50/50 to-white';
  }
  if (FORM_CORRECTION_STATUSES.includes(status)) return 'border-red-200/80 bg-gradient-to-br from-red-50/40 to-white';
  if (status === 'in_progress') return 'border-amber-200/60 bg-gradient-to-br from-amber-50/40 to-white';
  return 'border-slate-200/60 bg-gradient-to-br from-white to-slate-50/80';
}